import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, MapPin, Phone, Mail, User, 
  ShieldCheck, Save, Hash, Globe, Link, 
  Award, FileText, Upload, CheckCircle 
} from 'lucide-react';
import './CollegeDetailsForm.css';

const CollegeDetailsForm = () => {
  // --- STATE ---
  const [formData, setFormData] = useState({
    systemId: '',
    collegeRegNo: '',
    universityName: '',
    collegeName: '',
    website: '',       // NEW FIELD
    affiliation: '',   // NEW FIELD
    streetAddress: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
    collegePhone: '',
    collegeEmail: '',
    poName: '',
    poPhone: '',
    poEmail: '',
  });

  // --- NEW FILE STATES ---
  const [tradeLicense, setTradeLicense] = useState(null);
  const [otherDocs, setOtherDocs] = useState([]);
  
  // --- POPUP STATE ---
  const [showPopup, setShowPopup] = useState(false);

  const navigate = useNavigate();

  // --- LOGIC ---
  useEffect(() => {
    const generatedId = `SYS-${Math.floor(Math.random() * 1000000)}`;
    setFormData(prevData => ({ ...prevData, systemId: generatedId }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userId = localStorage.getItem('userId');
    if (!userId) {
      alert("Error: User ID not found. Please log in again.");
      return;
    }

    if (!tradeLicense) {
      alert("Please upload the mandatory Trade License document.");
      return;
    }

    // Use FormData because we are sending files
    const submitData = new FormData();
    Object.keys(formData).forEach(key => submitData.append(key, formData[key]));
    submitData.append('userId', userId);
    submitData.append('tradeLicense', tradeLicense);
    
    if (otherDocs && otherDocs.length > 0) {
      Array.from(otherDocs).forEach(file => submitData.append('otherDocs', file));
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/college/submit-details`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      console.log('Form Submitted:', response.data);
      
      // SHOW POPUP INSTEAD OF REDIRECTING
      setShowPopup(true);

    } catch (error) {
      console.error('Submission failed:', error.response?.data?.message || error.message);
      alert('Form submission failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    navigate('/'); // Redirect to login or home after acknowledging
  };

  return (
    <div id="college-details-page-wrapper">
      <div className="college-hero-backdrop"></div>
      
      <div className="premium-form-container">
        <div className="form-header-card">
          <h1>Institution Registration</h1>
          <p>Complete your college profile to access the placement network.</p>
        </div>

        <form onSubmit={handleSubmit} className="details-form-grid">
          
          {/* Section 1: System Generated ID */}
          <div className="form-section-card highlight-border">
            <div className="section-header">
              <ShieldCheck className="section-icon" size={20} />
              <h3>System Verification</h3>
            </div>
            <div className="input-grid">
              <div className="input-group full-width">
                <label htmlFor="systemId">System Generated ID (Auto-Filled)</label>
                <div className="input-wrapper locked-input">
                  <Hash size={16} />
                  <input 
                    type="text" id="systemId" name="systemId" value={formData.systemId} readOnly className="read-only-field"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: College Information */}
          <div className="form-section-card">
            <div className="section-header">
              <Building2 className="section-icon" size={20} />
              <h3>College Information</h3>
            </div>
            <div className="input-grid">
              <div className="input-group full-width">
                <label htmlFor="collegeRegNo">College Registration No *</label>
                <div className="input-wrapper">
                  <Hash size={16} />
                  <input type="text" id="collegeRegNo" name="collegeRegNo" value={formData.collegeRegNo} onChange={handleChange} required placeholder="e.g. REG-2024-001" />
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="universityName">University Name *</label>
                <div className="input-wrapper">
                  <Building2 size={16} />
                  <input type="text" id="universityName" name="universityName" value={formData.universityName} onChange={handleChange} required />
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="collegeName">College Name *</label>
                <div className="input-wrapper">
                  <Building2 size={16} />
                  <input type="text" id="collegeName" name="collegeName" value={formData.collegeName} onChange={handleChange} required />
                </div>
              </div>

              {/* NEW: Website and Affiliation */}
              <div className="input-group">
                <label htmlFor="website">Website (Optional)</label>
                <div className="input-wrapper">
                  <Link size={16} />
                  <input type="url" id="website" name="website" value={formData.website} onChange={handleChange} placeholder="https://www.example.edu" />
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="affiliation">Affiliation / Accreditation (Optional)</label>
                <div className="input-wrapper">
                  <Award size={16} />
                  <input type="text" id="affiliation" name="affiliation" value={formData.affiliation} onChange={handleChange} placeholder="e.g., AICTE, NAAC Grade A" />
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Address Information */}
          <div className="form-section-card">
            <div className="section-header">
              <MapPin className="section-icon" size={20} />
              <h3>Campus Address</h3>
            </div>
            <div className="input-grid">
              <div className="input-group full-width">
                <label htmlFor="streetAddress">Street Address *</label>
                <input type="text" id="streetAddress" name="streetAddress" value={formData.streetAddress} onChange={handleChange} required />
              </div>
              <div className="input-group">
                <label htmlFor="city">City *</label>
                <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} required />
              </div>
              <div className="input-group">
                <label htmlFor="state">State *</label>
                <input type="text" id="state" name="state" value={formData.state} onChange={handleChange} required />
              </div>
              <div className="input-group">
                <label htmlFor="pincode">Pincode *</label>
                <input type="text" id="pincode" name="pincode" value={formData.pincode} onChange={handleChange} required />
              </div>
              <div className="input-group">
                <label htmlFor="country">Country *</label>
                <div className="input-wrapper">
                  <Globe size={16} />
                  <input type="text" id="country" name="country" value={formData.country} onChange={handleChange} required />
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Contact Information */}
          <div className="form-section-card">
            <div className="section-header">
              <Phone className="section-icon" size={20} />
              <h3>Official Contact</h3>
            </div>
            <div className="input-grid">
              <div className="input-group">
                <label htmlFor="collegePhone">College Phone *</label>
                <div className="input-wrapper">
                  <Phone size={16} />
                  <input type="tel" id="collegePhone" name="collegePhone" value={formData.collegePhone} onChange={handleChange} required />
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="collegeEmail">College Email *</label>
                <div className="input-wrapper">
                  <Mail size={16} />
                  <input type="email" id="collegeEmail" name="collegeEmail" value={formData.collegeEmail} onChange={handleChange} required />
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Placement Officer Information */}
          <div className="form-section-card">
            <div className="section-header">
              <User className="section-icon" size={20} />
              <h3>Placement Officer Details</h3>
            </div>
            <div className="input-grid">
              <div className="input-group full-width">
                <label htmlFor="poName">Officer Name *</label>
                <div className="input-wrapper">
                  <User size={16} />
                  <input type="text" id="poName" name="poName" value={formData.poName} onChange={handleChange} required />
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="poPhone">Officer Phone *</label>
                <div className="input-wrapper">
                  <Phone size={16} />
                  <input type="tel" id="poPhone" name="poPhone" value={formData.poPhone} onChange={handleChange} required />
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="poEmail">Officer Email *</label>
                <div className="input-wrapper">
                  <Mail size={16} />
                  <input type="email" id="poEmail" name="poEmail" value={formData.poEmail} onChange={handleChange} required />
                </div>
              </div>
            </div>
          </div>

          {/* NEW SECTION: Document Uploads */}
          <div className="form-section-card">
            <div className="section-header">
              <FileText className="section-icon" size={20} />
              <h3>Verification Documents</h3>
            </div>
            <div className="input-grid">
              <div className="input-group full-width">
                <label>Trade License (Mandatory) *</label>
                <div className="premium-file-upload">
                  <Upload size={18} />
                  <input 
                    type="file" 
                    accept=".pdf,.jpg,.jpeg,.png" 
                    onChange={(e) => setTradeLicense(e.target.files[0])} 
                    required 
                  />
                  <span>{tradeLicense ? tradeLicense.name : "Click to select Trade License (PDF, JPG, PNG)"}</span>
                </div>
              </div>

              <div className="input-group full-width">
                <label>Other Documents (Optional - GST, Registration, etc.)</label>
                <div className="premium-file-upload">
                  <Upload size={18} />
                  <input 
                    type="file" 
                    accept=".pdf,.jpg,.jpeg,.png" 
                    multiple 
                    onChange={(e) => setOtherDocs(e.target.files)} 
                  />
                  <span>
                    {otherDocs && otherDocs.length > 0 
                      ? `${otherDocs.length} file(s) selected` 
                      : "Click to select additional documents"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <button type="submit" className="gold-submit-btn">
            <Save size={20} /> Submit for Verification
          </button>

        </form>
      </div>

      {/* SUCCESS POPUP MODAL */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-modal">
            <CheckCircle size={56} className="success-icon" />
            <h2>Registration Submitted</h2>
            <p>Thanks a lot for filling the form and submitting the documents for smooth verification process, our team will check and confirm at the earliest possible.</p>
            <button onClick={handleClosePopup} className="popup-close-btn">Return to Login</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegeDetailsForm;